import { Query, CreateQueryRequest, UpdateQueryRequest } from '../types/Query';
import { toCamelCase } from '../utils/caseConverter';
import { getApiHeaders } from '../utils/apiHeaders';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const extractQueryList = (data: any): Query[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.queries)) return data.queries;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

export const fetchQueries = (
  customerId?: number,
  params?: { updatedAt?: string; page?: number; pageSize?: number; limit?: number },
) => {
  const queryURI = customerId ? `/api/queries/customer/${customerId}` : '/api/queries';
  const url = new URL(`${API_BASE_URL}${queryURI}`);
  if (params?.updatedAt) {
    url.searchParams.set('updated_at', params.updatedAt);
  }
  if (params?.page) {
    url.searchParams.set('page', String(params.page));
  }
  const resolvedPageSize = params?.pageSize ?? params?.limit;
  if (resolvedPageSize) {
    url.searchParams.set('pageSize', String(resolvedPageSize));
  }
  return fetch(url.toString(), {
    headers: getApiHeaders('none')
  }).then(res => res.json()).then(data => toCamelCase(data));
};

export async function createQuery(data: CreateQueryRequest): Promise<Query> {
  const res = await fetch(`${API_BASE_URL}/api/queries`, {
    method: 'POST',
    headers: getApiHeaders('json'),
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to create query: ${errorText}`);
  }

  return toCamelCase(await res.json());
}

export async function assignQuery(queryId: number, userId: number): Promise<{ id: number }> {
  const res = await fetch(`${API_BASE_URL}/api/queries/${queryId}/assign`, {
    method: 'POST',
    headers: getApiHeaders('json'),
    body: JSON.stringify({ "assigned_to": userId })
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to assign query: ${errorText}`);
  }

  return res.json();
}

export async function updateQuery(queryId: number, payload: UpdateQueryRequest): Promise<Query> {
  const res = await fetch(`${API_BASE_URL}/api/queries/${queryId}`, {
    method: 'PATCH',
    headers: getApiHeaders('json'),
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to update query: ${errorText}`);
  }

  const responseData = await res.json();
  return toCamelCase(responseData);
}

export async function updateQueryStatus(queryId: number, status: string): Promise<Query> {
  return updateQuery(queryId, { status });
}

export async function fetchQueryById(queryId: number): Promise<Query> {
  const res = await fetch(`${API_BASE_URL}/api/queries/${queryId}`, {
    headers: getApiHeaders('none'),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to fetch query: ${errorText}`);
  }
  const data = await res.json();
  return toCamelCase(data);
}

export async function searchQueriesForBooking(params: {
  q: string;
  page?: number;
  limit?: number;
}): Promise<Query[]> {
  const url = new URL(`${API_BASE_URL}/api/queries/search`);
  url.searchParams.set('q', params.q);
  if (params.page) {
    url.searchParams.set('page', String(params.page));
  }
  if (params.limit) {
    url.searchParams.set('limit', String(params.limit));
  }

  const res = await fetch(url.toString(), {
    headers: getApiHeaders('none'),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to search queries: ${errorText}`);
  }

  const data = toCamelCase(await res.json());
  return extractQueryList(data);
}
